import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';

import GridContainer from '../../components/Grid/GridContainer.js';
import GridItem from '../../components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SnackbarCustom from '../../components/Snackbar/Snackbar.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaLoader from 'components/SparaLoader/SparaLoader.js';

import { AddAlert, Save } from '@mui/icons-material';
import Step1 from './AddUserSteps/Step1.js';

import { fetcher, companyUserApi } from '../../lib/api';

import wizardStyle from 'assets/jss/material-dashboard-pro-react/components/wizardStyle.js';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles(wizardStyle);

const AddCompanyUser = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const classes = useStyles();
    const step1Ref = useRef(null);

    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
    const [isSaving, setIsSaving] = useState(false);

    const { data: userData, isLoading: isLoadingUser } = useSWR(
        id ? companyUserApi.getByUser(id) : null,
        fetcher
    );

    const { data: companies, isLoading: isLoadingCompanies } = useSWR(
        '/company?page=1&loadMore=false',
        fetcher
    );

    const { data: specialCompanies, isLoading: isLoadingSpecialCompanies } = useSWR(
        '/special_company',
        fetcher
    );

    const { data: regions, isLoading: isLoadingRegions } = useSWR('/region', fetcher);

    const showNotification = (message, type = 'success') => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification((prev) => ({ ...prev, open: false })), 4000);
    };

    const checkEmail = async (login) => {
        try {
            const response = await fetcher(`/user/by_login/${login}`);
            if (response?.results?.id) {
                navigate(`/user/${response.results.id}`);
            } else {
                showNotification(response?.message || t('company_user.user_not_found'), 'warning');
            }
        } catch (error) {
            showNotification(
                error?.response?.data?.message || error?.message || t('company_user.search_error'),
                'warning'
            );
        }
    };

    const handleSave = async () => {
        if (!step1Ref.current?.sendState) return;

        const userData = step1Ref.current.sendState();
        const isUpdating = !!id;

        setIsSaving(true);
        try {
            await fetcher(
                isUpdating ? companyUserApi.update(userData.id) : companyUserApi.create,
                isUpdating ? 'PUT' : 'POST',
                {
                    torgid_company_ids: userData.companyIds,
                    group_ids: userData.groupIds,
                    role_ids: userData.role_ids,
                    torgid_user_id: userData.id,
                    open_for_barter: userData.open_for_barter,
                    hidden: userData.hidden,
                }
            );

            if (isUpdating) {
                mutate(companyUserApi.getByUser(id));
            }

            showNotification(t('common.saved_successfully'), 'success');
            navigate('/company-user');
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                t('common.error_occurred');
            showNotification(msg, 'warning');
        } finally {
            setIsSaving(false);
        }
    };

    const buildUserData = () => {
        if (!userData?.results) return {};

        const companyUsers = userData.results.torgid_company_users || [];
        return {
            ...userData.results,
            companyIds: companyUsers
                .filter((cu) => cu.torgid_company && !cu.torgid_special_company && cu.torgid_company_id !== 1305)
                .map((cu) => cu.torgid_company_id),
            groupIds: companyUsers
                .filter((cu) => cu.torgid_special_company)
                .map((cu) => cu.torgid_special_company_id),
        };
    };

    if (isLoadingUser || isLoadingCompanies || isLoadingSpecialCompanies || isLoadingRegions) {
        return <SparaLoader text={t('common.loading')} />;
    }

    const isUpdating = !!id;
    const finalUserData = buildUserData();

    return (
        <GridContainer>
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={isUpdating ? t('company_user.update_profile') : t('company_user.build_profile')}
                    rightComponent={
                        <SparaButton color="primary" icon={Save} onClick={handleSave} disabled={isSaving}>
                            {t('finish')}
                        </SparaButton>
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        <Step1
                            ref={step1Ref}
                            classes={classes}
                            user={finalUserData}
                            companies={companies || {}}
                            checkEmail={checkEmail}
                            data={regions || {}}
                            company={specialCompanies || {}}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid var(--spara-border, #e2e8f0)', marginTop: '8px' }}>
                            <SparaButton color="primary" icon={Save} onClick={handleSave} disabled={isSaving}>
                                {t('finish')}
                            </SparaButton>
                        </div>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default AddCompanyUser;
